import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { useState } from 'react';
import { useStore } from '../../store/storeHooks';
import { refProxy } from '../../store/refProxy';

export type OverlayVisibility = boolean | 'OnHover' | 'OnClick';

type Props = BindingProps<{
  visibility?: OverlayVisibility,
  visibilityTrigger?: boolean,
  makeExclusiveWhenVisible?: boolean,
  suppressFrom?: OverlayState[],
  // dirty hack
  onHide?: () => void,
  onShow?: () => void
}>
export type OverlayStateProps = Props;

export type OverlayAnchorJSXProps = Partial<{
  onMouseEnter: React.MouseEventHandler,
  onMouseLeave: React.MouseEventHandler,
  onClick: React.MouseEventHandler,
  onFocus: React.FocusEventHandler,
  onFocusCapture: React.FocusEventHandler,
  onBlur: React.FocusEventHandler
}>

export type OverlayEventHandlerName =
  'handleAnchorMouseEnter' |
  'handleAnchorMouseLeave' |
  'handleAnchorClick' |
  'handleAnchorFocus' |
  'handleAnchorBlur';

export class OverlayState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable(this, {
      suppressFrom: computed,
      visibilityTrigger: computed,
      anchorIsHovered: observable,
      anchorIsClicked: observable,
      anchorIsFocused: computed,
      anchorIsFocusedWithin: computed,
      isClickedLast: computed,
      isClickCapturedLast: computed,
      isOverlayClickCapturedLast: computed,
      isVisibleFromClickHandler: observable,
      isVisibleFromClick: computed,
      isVisibleFromHover: computed,
      overlayIsHovered: observable,
      isPlayerTutorialOn: observable,
      overlayIsFocused: computed,
      overlayIsFocusedWithin: computed,
      visibility: computed,
      makeExclusiveWhenVisible: computed,
      isVisible: computed,
      isExclusivelyVisible: computed,
      setPlayerTutorialMode: action,
      handleVisibilityTriggerChange: action,
      handleIsVisibleChange: action,
      handleAnchorMouseEnter: action,
      handleAnchorMouseLeave: action,
      handleAnchorClick: action,
      handleOverlayClick: action,
      handleAnchorFocus: action,
      handleAnchorBlur: action,
      hide: action,
      anchorProps: computed,
    });

    this.onPropChange('visibilityTrigger',
      this.handleVisibilityTriggerChange.bind(this));

    this.onFieldChange('isVisible',
      this.handleIsVisibleChange.bind(this));
  }

  readonly anchorRef = refProxy(this, 'anchor');
  readonly overlayRef = refProxy(this, 'overlay');
  readonly arrowRef = refProxy(this, 'arrow');

  get suppressFrom(): OverlayState[] {
    return this.getResolvedProp('suppressFrom', []);
  }

  get visibilityTrigger(): boolean {
    return this.getResolvedProp('visibilityTrigger', []);
  }

  anchorIsHovered = false;
  anchorIsClicked = false;
  get anchorIsFocused() {
    return this.anchorRef.isFocused;
  }
  get anchorIsFocusedWithin() {
    return this.anchorRef.isFocusedWithin;
  }

  get isClickedLast() {
    const { ui } = this.store;
    return (
      ui.isElementLastClicked(this.overlayRef?.node) ||
      ui.isElementLastClicked(this.anchorRef?.node))
  }
  get isClickCapturedLast() {
    const { ui } = this.store;
    return (
      ui.isElementLastClickCaptured(this.overlayRef?.node) ||
      ui.isElementLastClickCaptured(this.anchorRef?.node))
  }

  get isOverlayClickCapturedLast() {
    const { ui } = this.store;
    return (
      ui.isElementLastClickCaptured(this.overlayRef?.node))
  }

  isVisibleFromClickHandler = false;
  get isVisibleFromClick(): boolean {
    if (!this.isClickedLast)
      return false;
    return this.isVisibleFromClickHandler;
  }

  get isVisibleFromHover(): boolean {
    return this.anchorIsHovered;
  }

  overlayIsHovered = false;
  isPlayerTutorialOn = false;
  get overlayIsFocused() {
    return this.overlayRef.isFocused;
  }
  get overlayIsFocusedWithin() {
    return this.overlayRef.isFocusedWithin;
  }

  private readonly defaultVisibilityTransform: OverlayVisibility = 'OnHover';

  get visibility(): OverlayVisibility {
    return this.getResolvedProp('visibility', this.defaultVisibilityTransform);
  }

  get makeExclusiveWhenVisible(): boolean {
    return this.getResolvedProp('makeExclusiveWhenVisible', false);
  }

  get isVisible(): boolean {
    if (this.suppressFrom.find(ovr => ovr.isVisible))
      return false;

    switch (this.visibility) {
      case true:
      case false: return this.visibility;

      case 'OnHover':
        return this.isVisibleFromHover;
      case 'OnClick':
        return this.isVisibleFromClick;
    }
    return false;
  }

  get isExclusivelyVisible(): boolean {
    return this.makeExclusiveWhenVisible ? this.isVisible : false;
  }

  setPlayerTutorialMode(mode: boolean) {
    this.isPlayerTutorialOn = mode;
  }

  handleVisibilityTriggerChange(newVal: boolean, _oldVal: boolean) {
    if (newVal === false)
      this.hide();
  }

  handleIsVisibleChange(newVal: boolean, _oldVal: boolean) {
    if (newVal) {
      this.notifyShow();
    } else {
      this.notifyHide();
    }
  }

  handleAnchorMouseEnter(_evt?: React.MouseEvent) {
    this.anchorIsHovered = true;
  }

  handleAnchorMouseLeave(_evt?: React.MouseEvent) {
    this.anchorIsHovered = false;
  }

  handleAnchorClick(_evt?: React.MouseEvent) {
    if (this.isOverlayClickCapturedLast)
      return;

    if (this.isVisibleFromClick)
      this.isVisibleFromClickHandler = false;
    else
      this.isVisibleFromClickHandler = true;
  }

  handleOverlayClick(_evt: React.MouseEvent) { /* noop */ }

  handleAnchorFocus(_evt: React.FocusEvent) { /* noop */ }

  handleAnchorBlur(_evt: React.FocusEvent) { /* noop */ }

  hide() {
    this.isVisibleFromClickHandler = false;
    this.anchorIsHovered = false;
  }

  notifyHide() {
    const onHide = this.props?.onHide;
    if (typeof onHide === 'function')
      onHide();
    this.emit('hide');
  }

  notifyShow() {
    const onShow = this.props?.onShow;
    if (typeof onShow === 'function')
      onShow();
    this.emit('show');
  }

  get anchorProps(): OverlayAnchorJSXProps {
    const props: OverlayAnchorJSXProps = {};

    props.onMouseEnter = evt => this.handleAnchorMouseEnter(evt);
    props.onMouseLeave = evt => this.handleAnchorMouseLeave(evt);

    props.onClick = evt => this.handleAnchorClick(evt);
    props.onFocus = evt => this.handleAnchorFocus(evt);
    props.onBlur = evt => this.handleAnchorBlur(evt);

    return props;
  }
}

export function useOverlayState(props?: Props): OverlayState {

  const store = useStore();
  const init = new OverlayState(store, props);

  const [state] = useState(init);

  return state;
}